import { FormattedMessage } from "react-intl";
import { Grid, useMediaQuery } from "@mui/material";
import { Add, Create, Delete } from "@mui/icons-material";

import { ListAction, ListActions, StyledListItem, StyledListWrapper, StyledPagination } from "@framework/styled";
import { AchievementRuleStatus, IAchievementLevel, IAchievementRule, TokenType } from "@framework/types";
import { SearchWithFilters } from "@dzambalaorg/mui-form-search";
import { EntityInput } from "@dzambalaorg/mui-inputs-entity";
import { Breadcrumbs, PageHeader, ProgressOverlay } from "@dzambalaorg/mui-page-layout";
import { DeleteDialog } from "@dzambalaorg/mui-dialog-delete";
import { emptyStateString } from "@dzambalaorg/draft-js-utils";
import { getEmptyTemplate } from "@dzambalaorg/mui-inputs-asset";
import { useDialogDelete, useFetchQuery } from "@dzambalaorg/react-hooks";
import { IPaginationResult } from "@dzambalaorg/types-collection";
import { useGlobalDialogFormCallbacks } from "@framework/shared";
import { Button, FormGrid, List } from "@dzambalaorg/ui-kit";

import { AdminDialogKeys } from "../../../../../shared";
import {
  achievementsLevelsCreateFilter,
  achievementsLevelsEditFilter,
  prepareAchievementsLevelsInitialValues,
} from "./schemas";
import { achievementLevelsValidationSchema } from "./form/validation";

const ACHIEVEMENT_LEVEL_URL = "/achievements/levels";

const emptyAchievementRule = {
  achievementStatus: AchievementRuleStatus.ACTIVE,
} as IAchievementRule;

const emptyValues = {
  title: "",
  description: emptyStateString,
  amount: 0,
  achievementLevel: 1,
  achievementRule: emptyAchievementRule,
  reward: getEmptyTemplate(TokenType.ERC20),
};

export const AchievementLevels = () => {
  const isMd = useMediaQuery(theme => theme.breakpoints.down("md"));

  const { isLoading, take, skip, onChangePage, hasNextPage, loadMoreRef, allRows, totalCount, refetch } = useFetchQuery<
    IPaginationResult<IAchievementLevel>
  >(ACHIEVEMENT_LEVEL_URL, { enableInfiniteScroll: isMd });

  const { openDialog } = useGlobalDialogFormCallbacks<IAchievementLevel, IAchievementLevel>();
  const { handleDelete } = useDialogDelete<IAchievementLevel>();

  const achievementLevelCreate = () => {
    openDialog(AdminDialogKeys.ACHIEVEMENT_LEVEL_CREATE, emptyValues as IAchievementLevel, {
      refetch,
      initialValues: emptyValues as IAchievementLevel,
      message: "dialogs.create",
      testId: "AchievementLevelDialogForm",
      url: `${ACHIEVEMENT_LEVEL_URL}`,
      filter: achievementsLevelsCreateFilter,
      validationSchema: achievementLevelsValidationSchema,
      componentProps: {
        mode: "create",
      },
    });
  };

  const achievementLevelEdit = (level: IAchievementLevel) => () => {
    openDialog(AdminDialogKeys.ACHIEVEMENT_LEVEL_EDIT, level, {
      refetch,
      initialValues: level,
      message: "dialogs.edit",
      testId: "AchievementLevelDialogForm",
      url: `${ACHIEVEMENT_LEVEL_URL}/${level.id}`,
      filter: achievementsLevelsEditFilter,
      prepareInitialValues: prepareAchievementsLevelsInitialValues,
      validationSchema: achievementLevelsValidationSchema,
      componentProps: {
        mode: "edit",
      },
    });
  };

  return (
    <Grid>
      <Breadcrumbs path={["dashboard", "achievements", "achievements.levels"]} />

      <PageHeader message="pages.achievements.levels.title">
        <Button
          intent="secondary"
          startIcon={<Add />}
          onClick={achievementLevelCreate}
          data-testid="AchievementLevelCreateButton"
        >
          <FormattedMessage id="form.buttons.create" />
        </Button>
      </PageHeader>

      <SearchWithFilters initialValues={{ achievementRuleIds: [] }}>
        <FormGrid columns={[2]}>
          <EntityInput name="achievementRuleIds" controller="achievements/rules" multiple />
          <FormGrid.Empty />
        </FormGrid>
      </SearchWithFilters>

      <ProgressOverlay isLoading={isLoading}>
        <StyledListWrapper count={allRows.length} isLoading={isLoading}>
          {allRows.map(level => (
            <StyledListItem key={level.id}>
              <List.ItemText sx={{ width: 0.8 }}>{level.title}</List.ItemText>
              <List.ItemText sx={{ width: 0.1 }}>{level.amount}</List.ItemText>
              <ListActions>
                <ListAction
                  onClick={achievementLevelEdit(level)}
                  message="form.buttons.edit"
                  dataTestId="LevelEditButton"
                  icon={Create}
                />
                <ListAction
                  onClick={handleDelete(level)}
                  message="form.buttons.delete"
                  dataTestId="LevelDeleteButton"
                  icon={Delete}
                />
              </ListActions>
            </StyledListItem>
          ))}
        </StyledListWrapper>
      </ProgressOverlay>

      {!isMd && totalCount > take && (
        <StyledPagination
          shape="rounded"
          page={skip / take + 1}
          count={Math.ceil(totalCount / take)}
          onChange={onChangePage}
        />
      )}
      {isMd && hasNextPage && <div ref={loadMoreRef} style={{ height: "20px", margin: "20px 0" }} />}

      <DeleteDialog url={ACHIEVEMENT_LEVEL_URL} refetch={refetch} />
    </Grid>
  );
};
